import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "./ui/textarea";
import { Stars, StarInput } from "./Stars";
import { useAuth } from "../context/AuthContext";
import { API } from "../lib/shop";

const ReviewForm = ({ slug, myReview, onSaved }) => {
  const [rating, setRating] = useState(myReview?.rating || 0);
  const [comment, setComment] = useState(myReview?.comment || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Please pick a star rating");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/products/${slug}/reviews`, { rating, comment });
      toast.success(myReview ? "Review updated" : "Thanks for your review!");
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-sm border border-border bg-white p-5" data-testid="review-form">
      <h3 className="text-sm font-semibold">{myReview ? "Update your review" : "Rate this product"}</h3>
      <div className="mt-3">
        <StarInput value={rating} onChange={setRating} testId="review-star-input" />
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={1000}
        placeholder="Share your experience with this product (optional)"
        className="mt-3 rounded-sm"
        data-testid="review-comment-input"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="review-submit-button"
        className="mt-3 flex items-center gap-2 rounded-sm bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-secondary disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {myReview ? "Update review" : "Submit review"}
      </button>
    </form>
  );
};

export const ProductReviews = ({ slug }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => setData({ summary: { average: null, count: 0 }, reviews: [], can_review: false, my_review: null }));
  }, [slug, user]);

  if (!data) return null;

  const handleSaved = ({ review, summary }) => {
    setData((d) => ({
      ...d,
      summary,
      my_review: review,
      reviews: [review, ...d.reviews.filter((r) => r.id !== review.id)],
    }));
  };

  return (
    <section className="mt-12" data-testid="reviews-section">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Customer Reviews</h2>
        {data.summary.count > 0 ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="reviews-summary">
            <Stars value={data.summary.average} testId="reviews-average-stars" />
            <span className="font-semibold text-foreground">{data.summary.average}</span>
            ({data.summary.count} review{data.summary.count > 1 ? "s" : ""})
          </span>
        ) : (
          <span className="text-sm text-muted-foreground" data-testid="reviews-empty">No reviews yet</span>
        )}
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[380px_1fr]">
        <div>
          {!user && (
            <p className="rounded-sm border border-border bg-muted/50 p-4 text-sm text-muted-foreground" data-testid="review-signin-prompt">
              <Link to="/login" className="font-medium text-secondary hover:underline">Sign in</Link> to review — only verified buyers can rate products.
            </p>
          )}
          {user && data.can_review && <ReviewForm slug={slug} myReview={data.my_review} onSaved={handleSaved} />}
          {user && !data.can_review && (
            <p className="rounded-sm border border-border bg-muted/50 p-4 text-sm text-muted-foreground" data-testid="review-not-eligible">
              Reviews are open to verified buyers. Once you've purchased this product, you can rate it here.
            </p>
          )}
        </div>

        <div>
          {data.reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">Be the first verified buyer to review this product.</p>
          ) : (
            <ul className="space-y-4">
              {data.reviews.map((r) => (
                <li key={r.id} className="rounded-sm border border-border bg-white p-4" data-testid={`review-item-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Stars value={r.rating} size="h-3.5 w-3.5" />
                    <span className="text-sm font-semibold">{r.reviewer_name}</span>
                    <span className="flex items-center gap-1 rounded-sm bg-[#16a34a]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#16a34a]">
                      <BadgeCheck className="h-3 w-3" /> Verified buyer
                    </span>
                    <span className="text-xs text-muted-foreground">{r.created_at.slice(0, 10)}</span>
                  </div>
                  {r.comment && <p className="mt-2 text-sm text-muted-foreground">{r.comment}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};
